/**
 * tripWeather.ts — turn a day's forecast into a warning worth showing, or
 * nothing. A grey "21°" next to every day said nothing; a chip that says
 * "Rain likely" or "Hot: 38°" changes what you pack or when you leave.
 */

export interface DayForecastSummary {
  condition: string;
  max: number | null;
  min?: number | null;
  rainChancePct?: number | null;
  rainMm?: number | null;
  windKmh?: number | null;
}

export type WeatherWarningKind = 'storm' | 'rain' | 'heat' | 'wind' | 'cold';

export interface WeatherWarning {
  kind: WeatherWarningKind;
  label: string;
  /** Longer sentence for the tooltip / screen readers. */
  detail: string;
}

const HEAT_C = 35;
const FROST_C = 2;
const WIND_KMH = 60;
const RAIN_CHANCE_PCT = 60;
const RAIN_MM = 5;

/** The ONE warning for a day (most serious first), or null for an ordinary day. */
export function weatherWarning(f: DayForecastSummary | null | undefined): WeatherWarning | null {
  if (!f) return null;
  const c = (f.condition || '').toLowerCase();
  if (c.includes('thunder')) {
    return { kind: 'storm', label: 'Storms', detail: 'Thunderstorms forecast at that night’s stop' };
  }
  if (typeof f.max === 'number' && f.max >= HEAT_C) {
    const t = Math.round(f.max);
    return { kind: 'heat', label: `Hot: ${t}°`, detail: `Forecast top of ${t}° at that night’s stop` };
  }
  if (typeof f.windKmh === 'number' && f.windKmh >= WIND_KMH) {
    const w = Math.round(f.windKmh);
    return { kind: 'wind', label: `Wind ${w} km/h`, detail: `Gusts to ${w} km/h forecast — take care towing` };
  }
  const wet = c.includes('rain') || c.includes('shower') || c.includes('drizzle');
  const chance = typeof f.rainChancePct === 'number' ? f.rainChancePct : null;
  const mm = typeof f.rainMm === 'number' ? f.rainMm : null;
  if ((chance !== null && chance >= RAIN_CHANCE_PCT) || (mm !== null && mm >= RAIN_MM) || (wet && chance === null && mm === null)) {
    const label = chance !== null ? `Rain ${Math.round(chance)}%` : 'Rain likely';
    return { kind: 'rain', label, detail: 'Rain forecast at that night’s stop' };
  }
  if (typeof f.min === 'number' && f.min <= FROST_C) {
    const t = Math.round(f.min);
    return { kind: 'cold', label: `Cold night: ${t}°`, detail: `Overnight low of ${t}° forecast` };
  }
  return null;
}
